import { useMutation } from '@tanstack/react-query';
import {
  type AdminUserSearchResultDto,
  AuthApi,
} from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { SupportImpersonateCombobox } from '@/core/components/support/support-impersonate-combobox';
import { resetSessionCaches } from '@/core/hooks/reset-session-caches';
import { useErrorMessage } from '@/core/hooks/use-error-message';
import { useIsPlatformAdmin } from '@/core/hooks/use-is-platform-admin';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';
import { getApiValidationErrorCode } from '@/core/utils/api-error.utils';
import { AppIonPage } from '@/core/components/app-ion-page';

function SupportImpersonateBreadcrumb({
  domainLabel,
  pageLabel,
}: {
  domainLabel: string;
  pageLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {pageLabel}
    </>
  );
}

export function SupportImpersonatePage() {
  const dict = useDictionary();
  const locale = useLocale();
  const pageDict = dict.support.impersonate;
  const getErrorMessage = useErrorMessage();
  const isPlatformAdmin = useIsPlatformAdmin();
  const { navigateShell } = useNavigateShell();
  const setTokens = useAuthStore((state) => state.setTokens);
  const setUser = useAuthStore((state) => state.setUser);
  const currentUser = useAuthStore((state) => state.user);
  const [selectedUser, setSelectedUser] =
    useState<AdminUserSearchResultDto | null>(null);

  useEffect(() => {
    if (!isPlatformAdmin) {
      return;
    }
    if (currentUser?.impersonation) {
      navigateShell(shellPaths.moreHub);
    }
  }, [isPlatformAdmin, currentUser?.impersonation, navigateShell]);

  const startImpersonation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await AuthApi.startImpersonation({
        body: { userId },
      });
      return response.data;
    },
    onSuccess: async (data) => {
      if (!data?.token || !data.refreshToken) {
        toast.error(pageDict.error);
        return;
      }

      setTokens({
        accessToken: data.token,
        refreshToken: data.refreshToken,
      });
      resetSessionCaches();

      try {
        const meResponse = await AuthApi.me();
        setUser(meResponse.data);
        toast.success(pageDict.success);
        navigateShell(shellPaths.home, { intent: 'switchTab' });
      } catch {
        if (data.user) {
          setUser(data.user);
        }
        toast.success(pageDict.success);
        navigateShell(shellPaths.home, { intent: 'switchTab' });
      }
    },
    onError: (err) => {
      const errorCode = getApiValidationErrorCode(err, 'userId');
      toast.error(getErrorMessage(errorCode, { fallback: pageDict.error }));
    },
  });

  if (!isPlatformAdmin) {
    return <Navigate to={shellPaths.moreHub} replace />;
  }

  if (currentUser?.impersonation) {
    return <Navigate to={shellPaths.moreHub} replace />;
  }

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="gap-6 pb-8">
          <PageHeader
            breadcrumb={
              <SupportImpersonateBreadcrumb
                domainLabel={dict.nav.supportTools}
                pageLabel={dict.nav.supportImpersonate}
              />
            }
            title={pageDict.title}
            backHref={shellPaths.moreSupport}
          />
          <SettingsSection
            id="support-impersonate-user"
            title={pageDict.userLabel}
            grouped
            footer={pageDict.description}
          >
            <SettingsGroupRows>
              <SettingsGroupRow>
                <SupportImpersonateCombobox
                  selectedUser={selectedUser}
                  onSelect={setSelectedUser}
                  disabled={startImpersonation.isPending}
                  className="w-full max-w-sm"
                  placeholder={pageDict.placeholder}
                  searchPlaceholder={pageDict.searchPlaceholder}
                  emptyMessage={pageDict.emptyMessage}
                  loadingMessage={pageDict.loadingMessage}
                  locale={locale}
                />
              </SettingsGroupRow>
              <SettingsGroupRow separated>
                <Button
                  className="w-full sm:w-auto"
                  disabled={!selectedUser || startImpersonation.isPending}
                  onClick={() => {
                    if (!selectedUser) return;
                    startImpersonation.mutate(selectedUser.id);
                  }}
                >
                  {startImpersonation.isPending
                    ? pageDict.starting
                    : pageDict.startButton}
                </Button>
              </SettingsGroupRow>
            </SettingsGroupRows>
          </SettingsSection>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
